import { useState, useId, useEffect, useRef, useCallback, useMemo } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { ChevronRight, X, Sparkles, Trophy, Eye, Wrench, Gem, Globe, TrendingUp, Star } from "lucide-react";
import type { StandoutScoreData } from "@/lib/api";
import { ApexLogo } from "@/components/brand/ApexLogo";
import { publicNavigationHref } from "@/lib/public-navigation-href";
import { BRAND_GREEN, BRAND_GREEN_DIVIDER, APEX_ELITE_DARK_TEXT, APEX_ELITE_DARK_BG, APEX_ELITE_DARK_BORDER } from "@/lib/design-tokens";

interface StandoutScoreProps {
  score: StandoutScoreData;
  variant?: "inline" | "compact";
  detail?: boolean;
}

const MAX_APEX_SCORE = 100;
const apexDarkVars = {
  "--apex-brand-green": BRAND_GREEN,
  "--apex-elite-dark-text": APEX_ELITE_DARK_TEXT,
  "--apex-elite-dark-bg": APEX_ELITE_DARK_BG,
  "--apex-elite-dark-border": APEX_ELITE_DARK_BORDER,
} as CSSProperties;

// Dimension labels are i18n keys resolved with t() at render time (Task #7585).
const dimensions: Array<{ key: keyof StandoutScoreData; justKey: keyof StandoutScoreData; labelKey: string; maxScore: number; icon: typeof Sparkles }> = [
  { key: "significance_and_story", justKey: "justification_significance", labelKey: "carDetail.standout.significance", maxScore: 20, icon: Trophy },
  { key: "design_and_presence", justKey: "justification_design", labelKey: "carDetail.standout.design", maxScore: 20, icon: Eye },
  { key: "engineering_and_driving", justKey: "justification_engineering", labelKey: "carDetail.standout.engineering", maxScore: 20, icon: Wrench },
  { key: "build_durability_risk", justKey: "justification_build", labelKey: "carDetail.standout.build", maxScore: 10, icon: Star },
  { key: "rarity_and_spec_gravity", justKey: "justification_rarity", labelKey: "carDetail.standout.rarity", maxScore: 10, icon: Gem },
  { key: "cultural_relevance", justKey: "justification_cultural", labelKey: "carDetail.standout.cultural", maxScore: 10, icon: Globe },
  { key: "market_momentum", justKey: "justification_market", labelKey: "carDetail.standout.market", maxScore: 10, icon: TrendingUp },
];

function getTier(score: number) {
  if (score >= 90) return { label: "Elite", variant: "elite" as const };
  if (score >= 70) return { label: "Classic", variant: "classic" as const };
  if (score >= 50) return { label: "Watch", variant: "watch" as const };
  return { label: "", variant: "base" as const };
}

// Host listing cards retain their existing treatment; only car detail opts in.
function getLegacyCompactStyle(score: number) {
  if (score >= 90) return { block: BRAND_GREEN, divider: BRAND_GREEN_DIVIDER, arc: "#C9A84C", badgeBg: "rgba(90,62,158,0.1)", badgeText: "#5A3E9E" };
  if (score >= 70) return { block: BRAND_GREEN, divider: BRAND_GREEN_DIVIDER, arc: "#C9A84C", badgeBg: "rgba(140,106,42,0.12)", badgeText: "#8C6A2A" };
  return { block: "#3D2010", divider: "#5A3020", arc: "#C4915A", badgeBg: "rgba(122,48,16,0.1)", badgeText: "#7A3010" };
}

function useAnimatedNumber(target: number, duration: number = 1200, delay: number = 0) {
  const [value, setValue] = useState(0);
  const frameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const start = performance.now();
      const animate = (now: number) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(eased * target));
        if (progress < 1) {
          frameRef.current = requestAnimationFrame(animate);
        }
      };
      frameRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      clearTimeout(timeout);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, duration, delay]);

  return value;
}

function ModalScoreRing({ score, tier, isVisible }: { score: number; tier: ReturnType<typeof getTier>; isVisible: boolean }) {
  const gradientId = useId();
  const r = 52;
  const circumference = 2 * Math.PI * r;
  const pct = Math.min((score / MAX_APEX_SCORE) * 100, 100);
  const dashLen = (pct / 100) * circumference;
  const animatedScore = useAnimatedNumber(isVisible ? score : 0, 1400, 200);

  return (
    <div className="relative w-32 h-32 shrink-0">
      <svg viewBox="0 0 128 128" className="w-full h-full">
        <circle cx="64" cy="64" r={r} fill="none" stroke="var(--apex-track)" strokeWidth="6" />
        <circle
          cx="64" cy="64" r={r} fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth="6"
          strokeLinecap="round"
          transform="rotate(-90 64 64)"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={isVisible ? `${circumference - dashLen}` : `${circumference}`}
          style={{ transition: "stroke-dashoffset 1.4s cubic-bezier(0.4, 0, 0.2, 1) 0.2s" }}
        />
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--apex-ring)" />
            <stop offset="100%" stopColor="var(--apex-ring)" stopOpacity="0.55" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-bold text-neutral-900 dark:text-neutral-100 leading-none tabular-nums"
          style={{ fontSize: 36, letterSpacing: "-1.5px" }}
        >
          {animatedScore}
        </span>
        <span
          className="font-semibold uppercase leading-none mt-1.5"
          style={{ fontSize: 9, letterSpacing: "0.22em", color: "var(--apex-chip-text)" }}
        >
          / {MAX_APEX_SCORE}
        </span>
      </div>
    </div>
  );
}

function ModalDimensionRow({
  dim,
  score,
  tier,
  index,
  isVisible,
  isExpanded,
  onToggle,
}: {
  dim: typeof dimensions[0];
  score: StandoutScoreData;
  tier: ReturnType<typeof getTier>;
  index: number;
  isVisible: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const rawVal = score[dim.key] as number | null;
  if (typeof rawVal !== "number" || rawVal == null) return null;
  // Legacy scorer data can exceed the axis max (e.g. 14 on a /10 axis) —
  // cap the DISPLAYED value at the max (owner call 2026-08-13).
  const val = Math.min(rawVal, dim.maxScore);

  const pct = dim.maxScore > 0 ? Math.min((val / dim.maxScore) * 100, 100) : 0;
  const justification = score[dim.justKey] as string | null;
  const Icon = dim.icon;
  const delay = 250 + index * 60;

  return (
    <div
      className="transition-[opacity,transform] duration-500"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(8px)",
        transitionDelay: `${delay}ms`,
      }}
    >
      <button
        type="button"
        onClick={justification ? onToggle : undefined}
        className={cn(
          "w-full text-left rounded-lg px-3 py-3 transition-colors duration-200",
           justification ? "cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-900" : "cursor-default",
           isExpanded && "bg-neutral-50 dark:bg-neutral-900"
        )}
      >
        <div className="flex items-center gap-3">
          <div
             className="flex items-center justify-center w-8 h-8 rounded-md shrink-0 bg-neutral-100 dark:bg-neutral-800"
          >
            <Icon className="w-4 h-4" style={{ color: "var(--apex-ring)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5">
               <span className="text-[13px] font-medium text-neutral-800 dark:text-neutral-100">{t(dim.labelKey)}</span>
               <span className="text-[13px] tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                 {val}<span className="text-neutral-400 dark:text-neutral-500">/{dim.maxScore}</span>
              </span>
            </div>
             <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
              <div
                className="h-full rounded-full"
                style={{
                  width: isVisible ? `${pct}%` : "0%",
                  background: "var(--apex-ring)",
                  transition: `width 0.9s cubic-bezier(0.4, 0, 0.2, 1) ${delay + 150}ms`,
                }}
              />
            </div>
          </div>
          {justification && (
            <ChevronRight
              className="w-4 h-4 text-neutral-400 shrink-0 transition-transform duration-200"
              style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
            />
          )}
        </div>
      </button>
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out"
        style={{
          maxHeight: isExpanded ? "200px" : "0px",
          opacity: isExpanded ? 1 : 0,
        }}
      >
        {justification && (
           <p className="px-3 pl-[56px] pb-3 pt-0.5 text-[13px] leading-relaxed text-neutral-600 dark:text-neutral-300">
            {justification}
          </p>
        )}
      </div>
    </div>
  );
}

function ApexFullScreen({ score, onClose, detail }: { score: StandoutScoreData; onClose: () => void; detail: boolean }) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [expandedDim, setExpandedDim] = useState<string | null>(null);
  const totalScore = score.total_score;
  const tier = getTier(totalScore);

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => setIsVisible(true)));
  }, []);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setIsVisible(false);
    setTimeout(onClose, 250);
  }, [onClose]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", handleEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [handleClose]);

  const tags = useMemo(() => {
    const t: string[] = [];
    if (tier.label) t.push(tier.label);
    if (score.collector_tier && score.collector_tier !== tier.label) t.push(score.collector_tier);
    if (score.era_style) t.push(score.era_style);
    return t;
  }, [score, tier]);

  const audienceTags = useMemo(() => {
    const a: string[] = [];
    if (score.audience_appeal) a.push(...score.audience_appeal);
    if (score.lifestyle_categories) a.push(...score.lifestyle_categories);
    return a.slice(0, 6);
  }, [score]);

  // Listing sections can create containing blocks through transforms, filters
  // or containment. Keep the viewport overlay outside those page ancestors.
  return createPortal(
    <div
      className={cn(
        `apex-score ${detail ? "apex-detail" : ""} apex-tier-${tier.variant} fixed inset-0 z-[100] flex items-center justify-center p-0 sm:p-6 transition-opacity duration-300`,
        isVisible && !isClosing ? "opacity-100" : "opacity-0"
      )}
      role="dialog"
      aria-modal="true"
      aria-label={t("carCard.apex_score_breakdown")}
      style={apexDarkVars}
    >
      <div
        className="absolute inset-0 material-scrim"
        onClick={handleClose}
      />

      <div
        className={cn(
          "relative z-10 w-full max-w-[600px] h-full max-h-none sm:h-auto sm:max-h-[90vh] flex flex-col rounded-none sm:rounded-2xl bg-white dark:bg-neutral-950 shadow-2xl ring-1 ring-black/5 transition-[opacity,transform] duration-300",
          isVisible && !isClosing ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-[0.98] opacity-0"
        )}
        style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      >
        <button
          onClick={handleClose}
           className="cursor-pointer absolute top-4 right-4 z-20 rounded-full p-2 bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
          aria-label={t("common.close")}
        >
           <X className="h-4 w-4 text-neutral-700 dark:text-neutral-200" />
        </button>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain" data-testid="apex-score-scroll">
          <div className="px-6 pt-7 pb-6 sm:px-8 sm:pt-8">
            <div
              className="flex items-center gap-2 mb-6 transition-opacity duration-500"
              style={{ opacity: isVisible ? 1 : 0, transitionDelay: "100ms" }}
            >
              <ApexLogo size={16} tone={detail ? "green" : "original"} className="h-4 w-4" />
              <span className="font-medium uppercase text-neutral-500 dark:text-neutral-400" style={{ fontSize: 10, letterSpacing: "0.18em" }}>DRIVESHARE</span>
              <span className="font-bold uppercase text-neutral-900 dark:text-neutral-100" style={{ fontSize: 12, letterSpacing: "0.1em" }}>{t("carCard.apex_score")}</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
              <ModalScoreRing score={totalScore} tier={tier} isVisible={isVisible} />
              <div className="flex-1 min-w-0">
                {tags.length > 0 && (
                  <div
                    className="flex flex-wrap items-center gap-2 mb-3 transition-opacity duration-500"
                    style={{ opacity: isVisible ? 1 : 0, transitionDelay: "200ms" }}
                  >
                    {tags.map((tag, i) => (
                      <span
                        key={i}
                        className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider border"
                        style={{
                          borderColor: "var(--apex-chip-border)",
                          background: "var(--apex-chip-bg)",
                          color: "var(--apex-chip-text)",
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {score.one_liner && (
                  <p
                    className="text-[15px] leading-snug text-neutral-800 dark:text-neutral-100 font-medium transition-opacity duration-500"
                    style={{ opacity: isVisible ? 1 : 0, transitionDelay: "260ms" }}
                  >
                    {score.one_liner}
                  </p>
                )}
              </div>
            </div>

            {score.listing_blurb && (
              <p
                 className="mt-5 text-[13px] leading-relaxed text-neutral-500 dark:text-neutral-300 transition-opacity duration-500"
                style={{ opacity: isVisible ? 1 : 0, transitionDelay: "320ms" }}
              >
                {score.listing_blurb}
              </p>
            )}
          </div>

           <div className="border-t border-neutral-200/80 dark:border-neutral-800" />

          <div className="px-6 py-6 sm:px-8">
            <div
              className="flex items-center gap-2 mb-4 transition-opacity duration-500"
              style={{ opacity: isVisible ? 1 : 0, transitionDelay: "200ms" }}
            >
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {t("carCard.score_breakdown")}
              </span>
              <div className="h-px flex-1 bg-neutral-200" />
            </div>

            <div className="space-y-0.5">
              {dimensions.map((dim, i) => (
                <ModalDimensionRow
                  key={dim.key}
                  dim={dim}
                  score={score}
                  tier={tier}
                  index={i}
                  isVisible={isVisible}
                  isExpanded={expandedDim === dim.key}
                  onToggle={() => setExpandedDim(expandedDim === dim.key ? null : (dim.key as string))}
                />
              ))}
            </div>

            {audienceTags.length > 0 && (
              <div
                className="mt-7 transition-[opacity,transform] duration-500"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "translateY(0)" : "translateY(8px)",
                  transitionDelay: "700ms",
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-3.5 h-3.5 text-neutral-400" />
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    {t("carCard.appeal_lifestyle")}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {audienceTags.map((tag, i) => (
                    <span
                      key={i}
                         className="rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wide bg-neutral-50 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div
               className="mt-7 pt-5 border-t border-neutral-200/80 dark:border-neutral-800 transition-opacity duration-500"
              style={{ opacity: isVisible ? 1 : 0, transitionDelay: "750ms" }}
            >
              <Link
                href={publicNavigationHref("/apex-score", import.meta.env.BASE_URL)}
                onClick={handleClose}
                 className="inline-flex items-center gap-1 text-[13px] font-medium text-neutral-600 dark:text-neutral-300 underline underline-offset-2 hover:text-neutral-900 dark:hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 rounded"
                data-testid="link-apex-learn-more"
              >
                {t("carCard.apex_learn_more")}
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ScoreArc({ score, size = "sm" }: { score: number; size?: "sm" | "lg" }) {
  const gradientId = useId();
  const pct = Math.min((score / MAX_APEX_SCORE) * 100, 100);
  const isSm = size === "sm";
  const r = 15.5;
  const circumference = 2 * Math.PI * r;
  const dashLen = (pct / 100) * circumference;

  return (
    <div className={cn("relative", isSm ? "h-11 w-11" : "h-20 w-20")}>
      <svg viewBox="0 0 36 36" className="h-full w-full -rotate-90">
        <circle cx="18" cy="18" r={r} fill="none" stroke="var(--apex-track)" strokeWidth="2" />
        <circle
          cx="18" cy="18" r={r} fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${dashLen} ${circumference - dashLen}`}
        />
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--apex-ring)" />
            <stop offset="100%" stopColor="var(--apex-ring)" stopOpacity="0.5" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn("font-semibold leading-none text-foreground", isSm ? "text-sm" : "text-2xl")}>{score}</span>
        {!isSm && <span className="text-xs mt-0.5 text-muted-foreground">/100</span>}
      </div>
    </div>
  );
}

const CIRCUMFERENCE = 2 * Math.PI * 22;

export function StandoutScore({ score, variant = "inline", detail = false }: StandoutScoreProps) {
  const { t } = useTranslation();
  const totalScore = score.total_score;
  if (!totalScore || totalScore < 1) return null;

  const tier = getTier(totalScore);
  const legacy = getLegacyCompactStyle(totalScore);
  const normalizedScore = Math.min(Math.round((totalScore / MAX_APEX_SCORE) * 100), 100);
  const [showModal, setShowModal] = useState(false);

  if (variant === "compact") {
    return (
      <>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className={`apex-score ${detail ? "apex-detail" : ""} apex-tier-${tier.variant} inline-flex items-stretch rounded-full overflow-hidden border hover:shadow-md transition-shadow group`}
          style={{ ...apexDarkVars, borderColor: detail ? "var(--apex-track)" : "#DDD5C0", background: detail ? "hsl(var(--card))" : "#F5F0E8", height: 60, fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          <div
            className="flex items-center justify-center relative shrink-0"
            style={{ width: 68, borderRadius: "100px 0 0 100px", background: detail ? "hsl(var(--card))" : legacy.block }}
          >
            <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full">
              <circle cx="28" cy="28" r="22" fill="none" stroke={detail ? "var(--apex-track)" : legacy.divider} strokeWidth="2" />
              <circle
                cx="28" cy="28" r="22" fill="none"
                stroke={detail ? "var(--apex-ring)" : legacy.arc} strokeWidth="2" strokeLinecap="round"
                transform="rotate(-90 28 28)"
                strokeDasharray={`${CIRCUMFERENCE}`}
                strokeDashoffset={`${CIRCUMFERENCE - (normalizedScore / 100) * CIRCUMFERENCE}`}
              />
            </svg>
            <div className="flex flex-col items-center gap-px z-10">
              <span className={`text-[21px] font-bold leading-none ${detail ? "text-foreground" : "text-white"}`} style={{ letterSpacing: "-1px" }}>{totalScore}</span>
              <span className="text-[6.5px] font-semibold uppercase leading-none" style={{ letterSpacing: "0.16em", color: detail ? "var(--apex-chip-text)" : legacy.arc }}>APEX</span>
            </div>
          </div>
          <div className="shrink-0" style={{ width: 1, background: detail ? "var(--apex-track)" : legacy.divider }} />
          <div className="flex flex-col justify-center" style={{ padding: "0 16px 0 14px", gap: 2 }}>
            <span className="inline-flex items-center gap-1 font-medium uppercase leading-none" style={{ fontSize: 8, letterSpacing: "0.12em", color: "#8C8070" }}>
              <ApexLogo size={10} tone={detail ? "green" : "original"} className="h-2.5 w-2.5" />
              DRIVESHARE
            </span>
            <span className="font-bold uppercase" style={{ fontSize: 12, letterSpacing: "0.06em", color: detail ? "var(--apex-identity)" : BRAND_GREEN, lineHeight: 1.1 }}>{t("carCard.apex_score")}</span>
            {tier.label && (
              <span
                className="font-semibold uppercase leading-none w-fit"
                style={{ fontSize: 7.5, letterSpacing: "0.1em", padding: "2px 6px", borderRadius: 3, ...(detail ? { border: "1px solid var(--apex-chip-border)", background: "var(--apex-chip-bg)", color: "var(--apex-chip-text)" } : { background: legacy.badgeBg, color: legacy.badgeText }) }}
              >
                {tier.label}
              </span>
            )}
          </div>
          <div className="flex items-center justify-center shrink-0" style={{ width: 38, borderLeft: detail ? "1px solid var(--apex-track)" : "1px solid #DDD5C0" }}>
            <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
              <path d="M1 1l4 4-4 4" stroke="#8C8070" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </button>
        {showModal && <ApexFullScreen score={score} detail={detail} onClose={() => setShowModal(false)} />}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`apex-score ${detail ? "apex-detail" : ""} apex-tier-${tier.variant} group flex w-full items-center gap-4 rounded-xl border border-border bg-gradient-to-r from-muted/80 to-background p-4 transition-shadow hover:border-border hover:shadow-md`}
        style={apexDarkVars}
      >
        <ScoreArc score={totalScore} size="lg" />
        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2">
            <ApexLogo size={16} tone={detail ? "green" : "original"} className="h-4 w-4" />
            <div className="flex items-baseline gap-1">
              <span className="text-[8px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">DRIVESHARE</span>
              <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-foreground">APEX</span>
            </div>
            {tier.label && (
              <span className="rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wider border" style={{ color: "var(--apex-chip-text)", background: "var(--apex-chip-bg)", borderColor: "var(--apex-chip-border)" }}>
                {tier.label}
              </span>
            )}
          </div>
          {score.one_liner && (
            <p className="mt-1.5 text-[13px] leading-snug text-muted-foreground line-clamp-2">{score.one_liner}</p>
          )}
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground/50 group-hover:text-muted-foreground transition-colors shrink-0" />
      </button>
      {showModal && <ApexFullScreen score={score} detail={detail} onClose={() => setShowModal(false)} />}
    </>
  );
}
